import React, { useEffect, useMemo, useState } from 'react';
import { combineClasses } from '../utils/formatters';
import {
  PANCHANG_EVENT_META,
  PANCHANG_SECTION_COPY
} from '../config/panchangConfig';
import { RUNTIME_CONFIG } from '../config';
import {
  getPanchangMonthOptions,
  getMonthlyPanchangHighlights
} from '../adapters/panchangAdapter';
import { panchangService } from '../services';

const PanchangHighlightsSection = ({ city }) => {
  const monthOptions = useMemo(() => getPanchangMonthOptions(new Date(), 6), []);
  const [selectedMonthKey, setSelectedMonthKey] = useState(monthOptions[0]?.key || '');
  const [panchangData, setPanchangData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const selectedMonth = useMemo(() => {
    return monthOptions.find((option) => option.key === selectedMonthKey) || monthOptions[0] || null;
  }, [monthOptions, selectedMonthKey]);

  const fallbackData = useMemo(() => {
    if (!selectedMonth) {
      return { entries: [], today: null, monthLabel: '', city };
    }

    return getMonthlyPanchangHighlights({
      year: selectedMonth.year,
      monthIndex: selectedMonth.monthIndex,
      city
    });
  }, [selectedMonth, city]);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      if (!selectedMonth) {
        setPanchangData(null);
        return;
      }

      setLoading(true);
      setApiError('');

      try {
        const liveData = await panchangService.getMonthlyHighlights({
          year: selectedMonth.year,
          monthIndex: selectedMonth.monthIndex,
          city
        });

        if (active) {
          setPanchangData(liveData);
        }
      } catch (error) {
        if (!active) {
          return;
        }

        const message = error?.message || 'Unable to load live Panchang data.';
        setApiError(message);
        if (RUNTIME_CONFIG.PANCHANG_ENABLE_FALLBACK) {
          setPanchangData(fallbackData);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [selectedMonth, city, fallbackData]);

  const resolvedData = panchangData || fallbackData;

  const todayTypeMeta = PANCHANG_EVENT_META[resolvedData.today?.type] || PANCHANG_EVENT_META.auspicious;

  return (
    <section className="relative overflow-hidden border-y border-amber-100 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50">
      <div className="pointer-events-none absolute -left-12 top-0 h-36 w-36 rounded-full bg-amber-200/40 blur-2xl"></div>
      <div className="pointer-events-none absolute right-0 top-8 h-40 w-40 rounded-full bg-rose-200/40 blur-2xl"></div>

      <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 inline-flex items-center rounded-full border border-amber-200 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
              Spiritual Calendar
            </p>
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{PANCHANG_SECTION_COPY.title}</h2>
            <p className="mt-2 max-w-2xl text-sm text-gray-600 sm:text-base">{PANCHANG_SECTION_COPY.subtitle}</p>
            <p className="mt-1 text-xs text-gray-500">
              {PANCHANG_SECTION_COPY.cityNoticePrefix}: <span className="font-medium text-gray-700">{resolvedData.city}</span>
            </p>
            {loading && <p className="mt-1 text-xs text-amber-700">Loading live Panchang data...</p>}
            {apiError && (
              <p className="mt-1 text-xs text-orange-700">
                {RUNTIME_CONFIG.PANCHANG_ENABLE_FALLBACK
                  ? 'Showing fallback Panchang data while live source is unavailable.'
                  : apiError}
              </p>
            )}
          </div>

          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">Select Month</label>
            <select
              value={selectedMonthKey}
              onChange={(event) => setSelectedMonthKey(event.target.value)}
              className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-amber-300 focus:outline-none sm:min-w-52"
            >
              {monthOptions.map((option) => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <article className="rounded-2xl border border-amber-200 bg-white/85 p-4 shadow-sm lg:col-span-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{PANCHANG_SECTION_COPY.todayCardTitle}</p>

            {resolvedData.today ? (
              <>
                <div className="mt-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{resolvedData.today.title}</p>
                    <p className="text-xs text-gray-500">{resolvedData.today.dayLabel}</p>
                  </div>
                  <span className={combineClasses('rounded-full border px-2 py-1 text-[11px] font-semibold', todayTypeMeta.badgeClass)}>
                    {todayTypeMeta.label}
                  </span>
                </div>

                <p className="mt-3 text-sm text-gray-700">{resolvedData.today.note}</p>
                <p className="mt-4 text-xs font-medium text-amber-700">Month: {resolvedData.monthLabel}</p>
              </>
            ) : (
              <p className="mt-3 text-sm text-gray-500">Panchang details are currently unavailable.</p>
            )}
          </article>

          <article className="rounded-2xl border border-orange-200 bg-white/85 p-4 shadow-sm lg:col-span-2">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{PANCHANG_SECTION_COPY.monthlyCardTitle}</p>
              <p className="text-xs font-medium text-gray-500">{resolvedData.monthLabel}</p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {resolvedData.entries.map((entry) => {
                const meta = PANCHANG_EVENT_META[entry.type] || PANCHANG_EVENT_META.auspicious;
                return (
                  <div key={entry.id} className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900">{entry.title}</p>
                      <span className={combineClasses('rounded-full border px-2 py-0.5 text-[11px] font-semibold', meta.badgeClass)}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{entry.dayLabel}</p>
                    <p className="mt-1 text-xs text-gray-600">{entry.note}</p>
                  </div>
                );
              })}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default PanchangHighlightsSection;
